import { ReactNode } from 'react';
import { QuickstartTheme } from '../../types/theme';
import { ResolvedDeviceFallbackConfig } from '../../types/deviceFallbackTheme';
import { ThemeLogo } from '../ThemeLogo';
import { FallbackStageMedia } from '../DeviceFallback/FallbackStageMedia';
import { MOBILE_STAGE_WIDTH } from '../DeviceFallback/mobileFallbackScale';
import { buildContinuumWash, buildMediaMask } from '../DeviceFallback/mobileStageWash';
import { ICON_DROP_MEDIA_ZOOM, ICON_DROP_PX, iconDropPx } from './iconDropScale';
import { resolveIconDropStageWash } from './iconDropWash';

interface IconDropStageProps {
  theme: QuickstartTheme;
  config: ResolvedDeviceFallbackConfig;
  children: ReactNode;
  /**
   * Width fed into `--stage-w`, the single scale source for every canvas pixel on
   * this stage (text and logos alike).
   *
   * Defaults to the viewport width (capped at 480px). When the phone is held
   * landscape and the stage is counter-rotated into a landscape frame, the
   * viewport width becomes the phone's LONG side, which would scale everything
   * ~2.2x. The rotated-frame caller passes the portrait width instead so the
   * composition stays identical to the upright case.
   */
  stageWidth?: string;
}

/**
 * The chrome every Icon Drop screen shares: campaign art running the top of the phone and
 * dissolving into the stage colour, with the Playcast credit pinned to the bottom.
 *
 * The art is shaped with a mask rather than a crop so the picture thins out instead of
 * stopping. Shared rather than copied per screen because the canvas draws the same frame
 * behind each one, and copies would drift apart as the mocks are iterated.
 *
 * Children are positioned against this element, so they place themselves absolutely.
 */
export const IconDropStage = ({ theme, config, children, stageWidth }: IconDropStageProps) => {
  const wash = resolveIconDropStageWash(theme);

  return (
    <div
      className='relative flex h-full min-h-0 w-full flex-col overflow-hidden font-["Inter"]'
      style={{
        backgroundColor: config.stageColor,
        ['--stage-w' as string]: stageWidth ?? MOBILE_STAGE_WIDTH,
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
      }}
    >
      <div
        className={wash.mediaBand === '100%' ? 'absolute inset-0' : 'absolute inset-x-0 top-0'}
        style={{
          ...(wash.mediaBand === '100%' ? {} : { height: wash.mediaBand }),
          maskImage: buildMediaMask(wash.maskStops),
          WebkitMaskImage: buildMediaMask(wash.maskStops),
        }}
      >
        <FallbackStageMedia
          theme={theme}
          blur={iconDropPx(ICON_DROP_PX.backgroundBlur)}
          anchor='top'
          zoom={ICON_DROP_MEDIA_ZOOM}
          fit='cover'
          cropLetterbox={false}
        />
      </div>

      <div
        className='pointer-events-none absolute inset-0'
        style={{ background: buildContinuumWash(config.stageColor, wash.washStops) }}
      />

      {children}

      <div
        className='absolute inset-x-0 z-10 flex justify-center'
        style={{ bottom: iconDropPx(ICON_DROP_PX.poweredByInset) }}
      >
        <ThemeLogo kind='poweredBy' theme={theme} style={{ height: iconDropPx(ICON_DROP_PX.poweredBy) }} />
      </div>
    </div>
  );
};
